/**
 * QZ Tray serial port abstraction.
 * Wraps qz.serial for listing ports, opening/closing connections,
 * and setting up data callbacks for scale communication.
 *
 * Protocol-specific serial parameters (rx framing, dataBits, parity)
 * are derived from the scale protocol to match manufacturer requirements.
 */
import qz from 'qz-tray';
import { parsearPeso } from './protocolosBalanza';

const PROTOCOL_CONFIGS = {
  toledo: {
    rx: { start: '\x02', end: '\x0D', width: null },
    dataBits: 7,
    parity: 'EVEN',
    stopBits: 1,
    flowControl: 'NONE',
    command: 'W\n',
  },
  zebra: {
    rx: { end: '\x0D', width: null },
    dataBits: 8,
    parity: 'NONE',
    stopBits: 1,
    flowControl: 'NONE',
    command: null,
  },
  cas: {
    rx: { end: '\x0D', width: null },
    dataBits: 8,
    parity: 'NONE',
    stopBits: 1,
    flowControl: 'NONE',
    command: null,
  },
  generico: {
    rx: { end: '\x0A', width: null },
    dataBits: 8,
    parity: 'NONE',
    stopBits: 1,
    flowControl: 'NONE',
    command: null,
  },
};

export function obtenerConfigProtocolo(protocolo) {
  return PROTOCOL_CONFIGS[protocolo] || PROTOCOL_CONFIGS.generico;
}

export async function listarPuertos() {
  try {
    if (!qz.websocket.isActive()) return [];
    const ports = await qz.serial.findPorts();
    return Array.isArray(ports) ? ports : [];
  } catch (err) {
    console.error('[QZ Serial] Error al listar puertos:', err?.message || err);
    return [];
  }
}

export async function conectarPuerto(puerto, { baudRate = 9600, protocolo } = {}) {
  try {
    if (!qz.websocket.isActive()) {
      console.warn('[QZ Serial] WebSocket inactivo');
      return false;
    }
    const cfg = obtenerConfigProtocolo(protocolo);
    await qz.serial.openPort(puerto, {
      rx: cfg.rx,
      baudRate,
      dataBits: cfg.dataBits,
      stopBits: cfg.stopBits,
      parity: cfg.parity,
      flowControl: cfg.flowControl,
    });
    console.log(`[QZ Serial] Puerto ${puerto} abierto (protocolo=${protocolo || 'default'}, baud=${baudRate})`);
    return true;
  } catch (err) {
    console.error(`[QZ Serial] Error al abrir puerto ${puerto}:`, err?.message || err);
    return false;
  }
}

export async function enviarDatos(puerto, data) {
  try {
    if (!qz.websocket.isActive()) return false;
    await qz.serial.sendData(puerto, data);
    return true;
  } catch (err) {
    console.error(`[QZ Serial] Error al enviar datos a ${puerto}:`, err?.message || err);
    return false;
  }
}

export async function enviarComandoProtocolo(puerto, protocolo) {
  const cfg = obtenerConfigProtocolo(protocolo);
  if (cfg.command) {
    return enviarDatos(puerto, cfg.command);
  }
  return false;
}

export async function desconectarPuerto(puerto) {
  try {
    if (!qz.websocket.isActive()) return;
    await qz.serial.closePort(puerto);
    console.log(`[QZ Serial] Puerto ${puerto} cerrado`);
  } catch (err) {
    console.error(`[QZ Serial] Error al cerrar puerto ${puerto}:`, err?.message || err);
  }
}

export function setCallbackSerial(fn) {
  qz.serial.setSerialCallbacks(fn);
}

/**
 * Try a single port+baudRate combination and return parsed weight data or null.
 * Opens the port, optionally sends a protocol command, waits for data, then closes.
 */
async function probarPuertoConTimeout(puerto, baudRate, protocolo, timeoutMs = 2500) {
  let resultado = null;

  setCallbackSerial((evt) => {
    if (evt?.type === 'ERROR') return;
    if (evt?.output) {
      const parsed = parsearPeso(protocolo, evt.output);
      if (parsed) resultado = parsed;
    }
  });

  const ok = await conectarPuerto(puerto, { baudRate, protocolo });
  if (!ok) return null;

  try {
    await enviarComandoProtocolo(puerto, protocolo);
    await new Promise(r => setTimeout(r, timeoutMs));
  } finally {
    await desconectarPuerto(puerto);
  }

  return resultado;
}

/**
 * Iterate available ports and common baud rates until valid weight data is received.
 * @param {string} protocolo - Scale protocol (zebra, toledo, cas, generico)
 * @param {(msg: string) => void} [onProgreso] - Progress callback
 * @returns {Promise<{puerto: string, baudRate: number, pesoInicial: object}|null>}
 */
export async function autoDetectarPuerto(protocolo, onProgreso) {
  const puertos = await listarPuertos();
  if (puertos.length === 0) return null;

  const baudRates = [9600, 4800, 19200, 38400];

  for (const puerto of puertos) {
    for (const baudRate of baudRates) {
      onProgreso?.(`Probando ${puerto} @ ${baudRate}...`);
      try {
        const resultado = await probarPuertoConTimeout(puerto, baudRate, protocolo, 2500);
        if (resultado) {
          return { puerto, baudRate, pesoInicial: resultado };
        }
      } catch (err) {
        console.warn(`[QZ Serial] Auto-detect fallo en ${puerto}@${baudRate}:`, err?.message);
      }
    }
  }

  return null;
}

const qzSerialService = {
  listarPuertos,
  conectarPuerto,
  enviarDatos,
  enviarComandoProtocolo,
  desconectarPuerto,
  setCallbackSerial,
  obtenerConfigProtocolo,
  autoDetectarPuerto,
};

export default qzSerialService;
